import { useTranslation } from 'react-i18next';
import { AuthWrapper, DynamicForm } from '@src/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FieldValues } from 'react-hook-form';
import { useCheckAuthResponse, useDynamicFormState } from '@src/hooks';
import { setPasswordSchema } from '@src/formSchemas/auth';
import { Button } from 'antd';
import { useAcceptMutation } from '@src/services/users/addUsers/addUserApiSlice';
import { useEffect } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

export function SetPassword() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const { checkAuthResponse } = useCheckAuthResponse();
    const {
        formRef,
        isDirty,
        isValid,
        isValidating,
        handleDataChange,
        handleSubmit,
    } = useDynamicFormState();

    const [accept, { isLoading }] = useAcceptMutation();
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token') || '';

    useEffect(() => {
        const subscription = formRef.current?.watch((_, { name }) => {
            if (name === 'password') {
                formRef.current?.trigger('confirmPassword');
            }
        });

        return () => subscription && subscription.unsubscribe?.();
    }, []);

    const onSubmit = async (formData: FieldValues) => {
        try {
            const response = await accept({
                password: formData.password,
                token: token,
            }).unwrap();

            checkAuthResponse(response, 'signUp');
            navigate('/');
        } catch (error: any) {
            checkAuthResponse(error, 'signUp');
        }
    };

    return (
        <AuthWrapper
            pageTitle={t('pages.auth.setPassword.title')}
            subtitle={t('pages.auth.setPassword.subTitle')}
        >
            <div className="flex flex-col ">
                <DynamicForm
                    ref={formRef}
                    useFormOptions={{
                        mode: 'onChange',
                        reValidateMode: 'onChange',
                    }}
                    formData={{
                        password: '',
                        confirmPassword: '',
                    }}
                    formKeyName="set-password-form"
                    onHandleSubmit={onSubmit}
                    containerClassNames="grid gap-4"
                    formRows={setPasswordSchema({ t })}
                    hasDataChanged={handleDataChange}
                    formFooter={
                        <div className="w-full flex flex-col justify-center mt-8 space-x-2 item">
                            <Button
                                size="large"
                                className="btn-primary h-14 justify-between"
                                loading={isLoading}
                                htmlType="button"
                                onClick={() => handleSubmit(onSubmit)}
                                disabled={!isDirty || !isValid || isValidating}
                                icon={
                                    <ChevronRightIcon width={16} height={16} />
                                }
                                iconPosition="end"
                            >
                                {t('buttons.setPassword')}
                            </Button>
                        </div>
                    }
                />
            </div>
        </AuthWrapper>
    );
}
